import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Plus, Activity } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useApi } from "@/hooks/useApi";
import { getBotStats, getBotStatus, splitStatus } from "@/api/bot";
import { DISCORD_INVITE_URL, SUPPORT_SERVER_URL, LOGO_URL } from "@/config";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { GlassCard } from "@/components/GlassCard";
import { StatCard } from "@/components/StatCard";
import { FEATURE_CATEGORIES } from "@/data/features";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroLogo = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [12, -12]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-12, 12]);

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative mx-auto flex h-64 w-64 items-center justify-center [perspective:900px] md:h-96 md:w-96"
      data-testid="hero-logo"
    >
      <div className="absolute inset-0 rounded-full border border-white/10 animate-spin-slower" aria-hidden="true">
        <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-white/70" />
      </div>
      <div className="absolute inset-6 rounded-full border border-white/5" aria-hidden="true" />
      <div className="absolute inset-0 rounded-full bg-white/[0.04] blur-3xl" aria-hidden="true" />
      <motion.div
        style={reduce ? undefined : { rotateX, rotateY }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
        className="relative"
      >
        <img
          src={LOGO_URL}
          alt="Xevon bot avatar"
          width={224}
          height={224}
          fetchPriority="high"
          decoding="async"
          className="h-40 w-40 rounded-full border border-white/20 object-cover shadow-[0_0_80px_rgba(255,255,255,0.12)] md:h-56 md:w-56"
        />
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="hero">
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        background:
          "radial-gradient(ellipse 60% 45% at 50% 0%, rgba(255,255,255,0.07), transparent 70%)",
      }}
    />
    <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-6 pb-20 pt-32 md:pt-48 lg:grid-cols-[1.2fr_1fr] lg:gap-16 lg:pb-32">
      <div>
        <MaskedLine delay={0.05}>
          <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-paper-500">
            <span className="h-px w-8 bg-white/30" aria-hidden="true" />
            The Next Generation Discord Bot
          </span>
        </MaskedLine>
        <h1
          className="mt-6 whitespace-nowrap font-display text-[clamp(3.75rem,7.2vw,8.5rem)] font-bold leading-[0.95] tracking-tight text-paper-100"
          data-testid="hero-title"
          aria-label="XEVON"
        >
          {"XEVON".split("").map((ch, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom" aria-hidden="true">
              <motion.span
                className="inline-block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.15 + i * 0.07, ease: EASE }}
              >
                {ch}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-8 max-w-xl text-base leading-relaxed text-paper-400 md:text-lg" data-testid="hero-copy">
          <MaskedLine delay={0.55}>Powerful moderation. Advanced automation.</MaskedLine>
          <MaskedLine delay={0.65}>A better Discord experience.</MaskedLine>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
        >
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-add-xevon-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
          >
            <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" aria-hidden="true" />
            Add Xevon
          </a>
          <a
            href={SUPPORT_SERVER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-support-button"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10"
          >
            Support Server
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </a>
        </motion.div>
      </div>
      <HeroLogo />
    </div>
  </section>
);

const formatNumber = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : n);

const StatsBand = () => {
  const { data, loading, error } = useApi(getBotStats);
  const stats = data
    ? [
        ["Servers", formatNumber(data.guilds), "stat-servers"],
        ["Members", formatNumber(data.users), "stat-members"],
        ["Commands", formatNumber(data.commands), "stat-commands"],
        ["Latency", `${data.latency}ms`, "stat-latency"],
        ["Uptime", data.uptime, "stat-uptime"],
        ["Shards", formatNumber(data.shards), "stat-shards"],
      ].filter(([, v]) => v !== undefined && v !== null)
    : [];

  return (
    <section className="mx-auto w-full max-w-7xl px-6 py-20" data-testid="stats-section">
      <Reveal>
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-paper-500">Live from the API</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl">
              Xevon in numbers
            </h2>
          </div>
          <span className="hidden items-center gap-2 font-mono text-xs text-paper-500 sm:flex">
            <Activity className="h-3.5 w-3.5" aria-hidden="true" />
            Real-time
          </span>
        </div>
      </Reveal>
      {loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="stats-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-2xl border border-white/5 bg-white/[0.03]" />
          ))}
        </div>
      )}
      {error && !loading && (
        <div
          className="rounded-2xl border border-white/10 bg-white/[0.02] px-8 py-14 text-center"
          data-testid="stats-unavailable"
          role="status"
        >
          <p className="font-display text-lg font-semibold text-paper-200">Stats unavailable</p>
          <p className="mt-2 text-sm text-paper-500">
            Unable to connect to Xevon services. Please try again later.
          </p>
        </div>
      )}
      {data && !loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map(([label, value, testId], i) => (
            <Reveal key={label} delay={i * 0.06}>
              <StatCard label={label} value={value} testId={testId} />
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
};

const CHAPTERS = [
  { n: "01", title: "Moderation, perfected", text: "Ban, kick, timeout, warn, purge — every action fast, logged, and fair. A toolkit your staff will actually enjoy using." },
  { n: "02", title: "Security without sleep", text: "Anti-nuke defense, whitelists, and trusted admins watch your server around the clock, so you don't have to." },
  { n: "03", title: "Support, built in", text: "Ticket panels, claiming, and transcripts turn your server into a proper help desk — no external tools required." },
  { n: "04", title: "Communities that grow", text: "Leveling, reaction roles, birthdays, and voice tools keep members active, rewarded, and coming back." },
];

const Manifesto = () => (
  <section className="mx-auto w-full max-w-7xl px-6 py-24 md:py-32" data-testid="manifesto">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-paper-500">The Manifesto</p>
      <h2 className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight text-paper-100 md:text-5xl">
        One bot. Every tool your server deserves.
      </h2>
    </Reveal>
    <div className="mt-20 space-y-0">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.n} delay={0.05}>
          <div
            className={`group grid gap-6 border-t border-white/10 py-12 transition-colors duration-500 hover:bg-white/[0.02] md:grid-cols-[120px_1fr_1.2fr] md:items-baseline md:gap-12 md:px-6 ${
              i === CHAPTERS.length - 1 ? "border-b" : ""
            }`}
            data-testid={`manifesto-chapter-${c.n}`}
          >
            <span className="font-mono text-sm text-paper-500">{c.n}</span>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-paper-100 transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
              {c.title}
            </h3>
            <p className="max-w-xl text-base leading-relaxed text-paper-400">{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FeaturesPreview = () => (
  <section className="mx-auto w-full max-w-7xl px-6 py-24" data-testid="features-preview">
    <Reveal>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-paper-500">Capabilities</p>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-paper-100 md:text-5xl">
            Sixteen modules. Zero bloat.
          </h2>
        </div>
        <Link
          to="/features"
          data-testid="features-preview-link"
          className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
        >
          All features
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      </div>
    </Reveal>
    <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {FEATURE_CATEGORIES.map((cat, i) => (
        <Reveal key={cat.id} delay={i * 0.05}>
          <Link to={`/features#${cat.id}`} data-testid={`feature-tile-${cat.id}`}>
            <GlassCard className="group flex h-full flex-col p-7">
              <cat.icon className="h-6 w-6 text-paper-100" aria-hidden="true" />
              <h3 className="mt-5 font-display text-lg font-semibold text-paper-100">{cat.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-paper-500">
                {cat.items.slice(0, 3).join(" · ")}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-paper-400 transition-colors duration-300 group-hover:text-paper-100">
                Explore
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </span>
            </GlassCard>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const StatusBand = () => {
  const { data, loading, error } = useApi(getBotStatus);
  const { overall, latency } = splitStatus(data);
  const allOk = data ? overall : false;

  return (
    <section className="mx-auto w-full max-w-7xl px-6 py-16" data-testid="status-band">
      <Reveal>
        <Link to="/status" data-testid="status-band-link">
          <GlassCard className="flex flex-col gap-6 p-8 sm:flex-row sm:items-center sm:justify-between" hover={false}>
            <div className="flex items-center gap-4">
              <span className="relative flex h-3 w-3">
                {!error && !loading && allOk && (
                  <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-white" />
                )}
                <span
                  className={`relative inline-flex h-3 w-3 rounded-full border ${
                    loading ? "border-white/30 bg-white/30" : error || !allOk ? "border-white/40 bg-transparent" : "border-white bg-white"
                  }`}
                />
              </span>
              <div>
                <p className="font-display text-lg font-semibold text-paper-100" data-testid="status-band-label">
                  {loading ? "Checking services…" : error ? "Status unavailable" : allOk ? "All systems operational" : "Partial service disruption"}
                </p>
                <p className="text-sm text-paper-500">
                  {loading ? "Contacting the Xevon API" : error ? "Unable to reach Xevon services" : `Live · ${latency ?? "—"}ms latency`}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-paper-400 transition-colors duration-300 hover:text-paper-100">
              View status page
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </GlassCard>
        </Link>
      </Reveal>
    </section>
  );
};

const CTA = () => (
  <section className="mx-auto w-full max-w-7xl px-6 py-24 md:py-36" data-testid="cta-section">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] px-8 py-20 text-center md:py-28">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{ background: "radial-gradient(ellipse 55% 60% at 50% 100%, rgba(255,255,255,0.08), transparent 70%)" }}
        />
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-paper-500">Ready when you are</p>
        <h2 className="mx-auto mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-paper-100 md:text-6xl">
          Upgrade your server in one click.
        </h2>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-add-xevon-button"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
          >
            <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" aria-hidden="true" />
            Add Xevon
          </a>
          <Link
            to="/commands"
            data-testid="cta-commands-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10"
          >
            Browse commands
          </Link>
        </div>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  useSEO({
    title: null,
    description:
      "Xevon — the next generation Discord bot. Powerful moderation, advanced automation, and a better Discord experience.",
    path: "/",
  });

  return (
    <>
      <Hero />
      <StatsBand />
      <Marquee items={["Moderation", "Security", "Tickets", "Leveling", "Social", "Voice", "Utility", "Fun"]} />
      <Manifesto />
      <FeaturesPreview />
      <StatusBand />
      <CTA />
    </>
  );
}
